import {
  Alert, Box, Button, Card, CardContent, Chip, Dialog, DialogActions,
  DialogContent, DialogTitle, FormControlLabel, IconButton, MenuItem,
  Stack, Switch, TextField, Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import VisibilityIcon from '@mui/icons-material/Visibility';

import { PageHeader } from '@/components/PageHeader';
import { DataState } from '@/components/DataState';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useApiQuery, useApiMutation } from '@/hooks/useApiQuery';
import { useToast } from '@/components/ToastProvider';
import { practice } from '@/api/endpoints';

/**
 * Intake form template builder.
 *
 * The template has a JSON schema with a list of fields. Each field has:
 *   - `key`            unique within form
 *   - `type`           text | textarea | number | select | multiselect | boolean | date | scale
 *   - `label`          patient-facing question
 *   - `helper`         optional help text
 *   - `required`       boolean
 *   - `options`        for select / multiselect
 *   - `min` / `max`    for number / scale
 *
 * The builder provides reorder + add/remove + per-field editor. Live preview
 * renders the form as the patient would see it. Tour 4 uses up/down arrows;
 * a future iteration adds proper drag-drop.
 *
 * We intentionally keep the schema flat (no conditional logic, no sections).
 * Anything more complex is a sign the template should be split into two.
 */

type FieldType = 'text' | 'textarea' | 'number' | 'select' | 'multiselect' | 'boolean' | 'date' | 'scale';

type Field = {
  key: string;
  type: FieldType;
  label: string;
  helper: string;
  required: boolean;
  options: string[];   // for select / multiselect
  min: number | null;  // for number / scale
  max: number | null;
};

type IntakeTemplate = {
  id: number;
  name: string;
  description: string;
  schema: { fields: Field[] };
  is_active: boolean;
  language: string;
};

const FIELD_TYPES: { code: FieldType; label: string }[] = [
  { code: 'text',        label: 'Single-line text' },
  { code: 'textarea',    label: 'Long text' },
  { code: 'number',      label: 'Number' },
  { code: 'select',      label: 'Single choice' },
  { code: 'multiselect', label: 'Multiple choice' },
  { code: 'boolean',     label: 'Yes / No' },
  { code: 'date',        label: 'Date' },
  { code: 'scale',       label: 'Scale (e.g. 0–10)' },
];

export function PracticeIntakeTemplatesPage() {
  const q = useApiQuery({
    queryKey: ['practice', 'intake-templates'],
    queryFn:  practice.intakeTemplates.list as () => Promise<IntakeTemplate[]>,
  });

  const [editing, setEditing] = useState<IntakeTemplate | null>(null);
  const [creating, setCreating] = useState(false);

  return (
    <>
      <PageHeader
        title="Intake form templates"
        subtitle="Forms patients fill out before a visit. Tied to procedures."
        actions={
          <Button variant="contained" startIcon={<AddIcon />}
                  onClick={() => setCreating(true)}>
            New template
          </Button>
        }
      />

      <DataState
        query={q}
        empty={{
          title: 'No intake templates yet',
          description: 'Create your first template — patients fill it before the visit, the clinician sees a summary.',
        }}
      >
        {(items) => (
          <Stack spacing={1.5}>
            {items.map((t) => <Row key={t.id} template={t} onEdit={() => setEditing(t)} />)}
          </Stack>
        )}
      </DataState>

      {(creating || editing) && (
        <BuilderDialog
          template={editing}
          onClose={() => { setEditing(null); setCreating(false); }}
        />
      )}
    </>
  );
}

function Row({
  template, onEdit,
}: { template: IntakeTemplate; onEdit: () => void }) {
  return (
    <Card>
      <CardContent>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box sx={{
            width: 40, height: 40, borderRadius: 1.5,
            bgcolor: 'background.subtle', color: 'primary.main',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <AssignmentTurnedInIcon />
          </Box>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <Typography variant="subtitle1" fontWeight={600} noWrap>
                {template.name}
              </Typography>
              {!template.is_active && (
                <Chip size="small" label="inactive" variant="outlined" />
              )}
              <Chip size="small" label={template.language || 'en'} variant="outlined" />
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {(template.schema?.fields ?? []).length} field(s)
              {template.description && ` · ${template.description}`}
            </Typography>
          </Box>
          <Button size="small" variant="text" startIcon={<EditIcon />} onClick={onEdit}>
            Edit
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Builder dialog
// ─────────────────────────────────────────────────────────────────────────────

function BuilderDialog({
  template, onClose,
}: { template: IntakeTemplate | null; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const isEdit = template !== null;

  const [name, setName] = useState(template?.name ?? '');
  const [description, setDescription] = useState(template?.description ?? '');
  const [language, setLanguage] = useState(template?.language ?? 'en');
  const [isActive, setIsActive] = useState(template?.is_active ?? true);
  const [fields, setFields] = useState<Field[]>(
    template?.schema?.fields?.length ? template.schema.fields : []);

  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    setName(template?.name ?? '');
    setDescription(template?.description ?? '');
    setLanguage(template?.language ?? 'en');
    setIsActive(template?.is_active ?? true);
    setFields(template?.schema?.fields?.length ? template.schema.fields : []);
  }, [template?.id]);

  const saveMut = useApiMutation({
    mutationFn: () => {
      const payload = {
        name, description, language, is_active: isActive,
        schema: { fields },
      };
      return isEdit
        ? practice.intakeTemplates.update(template!.id, payload)
        : practice.intakeTemplates.create(payload);
    },
    onSuccess: () => {
      toast.success(isEdit ? 'Template saved.' : 'Template created.');
      qc.invalidateQueries({ queryKey: ['practice', 'intake-templates'] });
      onClose();
    },
    onError: (e) => toast.error(e.message),
  });

  // Validation
  const errors = useMemo(() => {
    const out: string[] = [];
    if (!name.trim()) out.push('Template name is required.');
    if (fields.length === 0) out.push('Add at least one field.');
    const keys = new Set<string>();
    for (const f of fields) {
      if (!f.key.trim()) out.push(`A field is missing its key.`);
      else if (keys.has(f.key)) out.push(`Duplicate field key: ${f.key}`);
      else keys.add(f.key);
      if (!f.label.trim()) out.push(`Field "${f.key || '?'}" is missing a label.`);
      if ((f.type === 'select' || f.type === 'multiselect') && f.options.length < 2) {
        out.push(`Field "${f.key}" needs at least 2 options.`);
      }
    }
    return out;
  }, [name, fields]);
  const valid = errors.length === 0;

  // Mutators
  const addField = (afterIndex?: number) => {
    const newField: Field = {
      key: `field_${fields.length + 1}`,
      type: 'text', label: '', helper: '', required: false,
      options: [], min: null, max: null,
    };
    if (afterIndex == null) {
      setFields([...fields, newField]);
    } else {
      setFields([...fields.slice(0, afterIndex + 1), newField,
                 ...fields.slice(afterIndex + 1)]);
    }
  };
  const updateField = (i: number, patch: Partial<Field>) => {
    setFields(fields.map((f, j) => j === i ? { ...f, ...patch } : f));
  };
  const removeField = (i: number) => setFields(fields.filter((_, j) => j !== i));
  const moveUp = (i: number) => {
    if (i === 0) return;
    const next = [...fields];
    [next[i - 1], next[i]] = [next[i]!, next[i - 1]!];
    setFields(next);
  };
  const moveDown = (i: number) => {
    if (i === fields.length - 1) return;
    const next = [...fields];
    [next[i + 1], next[i]] = [next[i]!, next[i + 1]!];
    setFields(next);
  };

  return (
    <Dialog open onClose={() => !saveMut.isPending && onClose()} maxWidth="md" fullWidth>
      <DialogTitle>
        {isEdit ? 'Edit intake template' : 'New intake template'}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 0.5 }}>
          {/* Header */}
          <Card variant="outlined">
            <CardContent>
              <Stack spacing={2}>
                <TextField fullWidth required label="Name"
                           value={name} onChange={(e) => setName(e.target.value)} />
                <TextField fullWidth label="Description"
                           value={description}
                           onChange={(e) => setDescription(e.target.value)} />
                <Stack direction="row" spacing={2}>
                  <TextField select label="Language"
                             value={language}
                             onChange={(e) => setLanguage(e.target.value)}
                             sx={{ minWidth: 140 }}>
                    {['en', 'pl', 'de', 'fr', 'es', 'it'].map((l) => (
                      <MenuItem key={l} value={l}>{l.toUpperCase()}</MenuItem>
                    ))}
                  </TextField>
                  <FormControlLabel
                    control={<Switch checked={isActive}
                                     onChange={(_, v) => setIsActive(v)} />}
                    label="Active" />
                </Stack>
              </Stack>
            </CardContent>
          </Card>

          {/* Fields */}
          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center"
                   sx={{ mb: 1.5 }}>
              <Typography variant="overline" color="text.secondary">
                Fields ({fields.length})
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button size="small" variant="outlined"
                        startIcon={<VisibilityIcon />}
                        onClick={() => setPreviewOpen(true)}
                        disabled={fields.length === 0}>
                  Preview
                </Button>
                <Button size="small" variant="contained" startIcon={<AddIcon />}
                        onClick={() => addField()}>
                  Add field
                </Button>
              </Stack>
            </Stack>
            <Stack spacing={1.5}>
              {fields.map((field, i) => (
                <FieldEditor
                  key={i} field={field}
                  onChange={(patch) => updateField(i, patch)}
                  onRemove={() => removeField(i)}
                  onMoveUp={() => moveUp(i)}
                  onMoveDown={() => moveDown(i)}
                  canMoveUp={i > 0}
                  canMoveDown={i < fields.length - 1}
                />
              ))}
              {fields.length === 0 && (
                <Card variant="outlined">
                  <CardContent sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      Empty form. Add the first field above.
                    </Typography>
                  </CardContent>
                </Card>
              )}
            </Stack>
          </Box>

          {/* Validation summary */}
          {errors.length > 0 && (
            <Alert severity="warning" variant="outlined">
              <Typography variant="subtitle2">Fix before saving:</Typography>
              <Box component="ul" sx={{ pl: 2, m: 0 }}>
                {errors.map((e, i) => (<li key={i}><Typography variant="body2">{e}</Typography></li>))}
              </Box>
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saveMut.isPending}>Cancel</Button>
        <Button variant="contained"
                disabled={!valid || saveMut.isPending}
                onClick={() => saveMut.mutate()}>
          {isEdit ? 'Save' : 'Create'}
        </Button>
      </DialogActions>

      <PreviewDialog
        open={previewOpen} onClose={() => setPreviewOpen(false)}
        name={name} description={description} fields={fields}
      />
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Field editor
// ─────────────────────────────────────────────────────────────────────────────

function FieldEditor({
  field, onChange, onRemove, onMoveUp, onMoveDown, canMoveUp, canMoveDown,
}: {
  field: Field;
  onChange: (patch: Partial<Field>) => void;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  canMoveUp: boolean;
  canMoveDown: boolean;
}) {
  const [confirmRemove, setConfirmRemove] = useState(false);

  const needsOptions = field.type === 'select' || field.type === 'multiselect';
  const needsRange = field.type === 'number' || field.type === 'scale';

  return (
    <>
      <Card variant="outlined">
        <CardContent>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
            <Stack>
              <IconButton size="small" disabled={!canMoveUp} onClick={onMoveUp}>
                <ArrowUpwardIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" disabled={!canMoveDown} onClick={onMoveDown}>
                <ArrowDownwardIcon fontSize="small" />
              </IconButton>
            </Stack>
            <Box sx={{ flexGrow: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                  size="small" label="Key (id)" required
                  value={field.key}
                  onChange={(e) => onChange({ key: e.target.value.replace(/[^a-z0-9_]/g, '_') })}
                  sx={{ width: 160 }}
                />
                <TextField
                  size="small" select label="Type" value={field.type}
                  onChange={(e) => onChange({ type: e.target.value as FieldType })}
                  sx={{ width: 200 }}
                >
                  {FIELD_TYPES.map((t) => (
                    <MenuItem key={t.code} value={t.code}>{t.label}</MenuItem>
                  ))}
                </TextField>
                <FormControlLabel
                  control={<Switch size="small" checked={field.required}
                                   onChange={(_, v) => onChange({ required: v })} />}
                  label="Required"
                />
              </Stack>
            </Box>
            <IconButton size="small" color="error" onClick={() => setConfirmRemove(true)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Stack>

          <Stack spacing={1.5}>
            <TextField fullWidth required label="Question (label)"
                       value={field.label}
                       onChange={(e) => onChange({ label: e.target.value })}
                       placeholder="What's the main reason for your visit?" />
            <TextField fullWidth label="Helper text (optional)"
                       value={field.helper}
                       onChange={(e) => onChange({ helper: e.target.value })}
                       placeholder="Brief tip or example shown under the question." />
            {needsOptions && (
              <TextField
                fullWidth label="Options" required multiline minRows={2}
                helperText="One option per line."
                value={field.options.join('\n')}
                onChange={(e) => onChange({
                  options: e.target.value.split('\n').map((s) => s.trim()).filter(Boolean),
                })}
              />
            )}
            {needsRange && (
              <Stack direction="row" spacing={2}>
                <TextField label="Min" type="number" sx={{ width: 100 }}
                           value={field.min ?? ''}
                           onChange={(e) => onChange({
                             min: e.target.value === '' ? null : parseFloat(e.target.value),
                           })} />
                <TextField label="Max" type="number" sx={{ width: 100 }}
                           value={field.max ?? ''}
                           onChange={(e) => onChange({
                             max: e.target.value === '' ? null : parseFloat(e.target.value),
                           })} />
              </Stack>
            )}
          </Stack>
        </CardContent>
      </Card>

      <ConfirmDialog
        open={confirmRemove}
        title="Remove field?"
        description={`Remove "${field.label || field.key}". This cannot be undone within this draft.`}
        destructive
        confirmLabel="Remove"
        onCancel={() => setConfirmRemove(false)}
        onConfirm={() => { onRemove(); setConfirmRemove(false); }}
      />
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Preview dialog — renders the form as the patient would see it
// ─────────────────────────────────────────────────────────────────────────────

function PreviewDialog({
  open, onClose, name, description, fields,
}: {
  open: boolean; onClose: () => void;
  name: string; description: string; fields: Field[];
}) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Patient preview</DialogTitle>
      <DialogContent>
        <Box sx={{ p: 2, bgcolor: 'background.subtle', borderRadius: 2 }}>
          <Typography variant="h6" sx={{ mb: 0.5 }}>{name || 'Untitled form'}</Typography>
          {description && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {description}
            </Typography>
          )}
          <Stack spacing={2.5}>
            {fields.map((f, i) => <FieldPreview key={i} field={f} />)}
          </Stack>
        </Box>
        <Typography variant="caption" color="text.secondary"
                    sx={{ display: 'block', mt: 2 }}>
          This is what the patient sees. The form is rendered server-side
          via `forms.IntakeFormBuilder` from the same schema.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

function FieldPreview({ field }: { field: Field }) {
  const label = (
    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
      {field.label || '(unlabeled)'}
      {field.required && <Box component="span" sx={{ color: 'error.main', ml: 0.5 }}>*</Box>}
    </Typography>
  );
  const helper = field.helper && (
    <Typography variant="caption" color="text.secondary"
                sx={{ display: 'block', mt: 0.5 }}>
      {field.helper}
    </Typography>
  );
  let control: React.ReactNode = null;
  switch (field.type) {
    case 'text':
      control = <TextField fullWidth size="small" disabled placeholder="…" />;
      break;
    case 'textarea':
      control = <TextField fullWidth size="small" multiline minRows={3} disabled placeholder="…" />;
      break;
    case 'number':
      control = <TextField fullWidth size="small" type="number" disabled
                           placeholder={field.min != null && field.max != null
                             ? `${field.min}–${field.max}` : ''} />;
      break;
    case 'date':
      control = <TextField fullWidth size="small" type="date" disabled />;
      break;
    case 'boolean':
      control = <FormControlLabel control={<Switch disabled />} label="Yes" />;
      break;
    case 'select':
    case 'multiselect':
      control = (
        <Stack direction="row" spacing={1} flexWrap="wrap">
          {field.options.map((o, i) => (
            <Chip key={i} label={o} variant="outlined" />
          ))}
        </Stack>
      );
      break;
    case 'scale':
      control = (
        <Stack direction="row" spacing={0.5}>
          {Array.from({ length: (field.max ?? 10) - (field.min ?? 0) + 1 }, (_, i) => (field.min ?? 0) + i)
            .map((n) => <Chip key={n} label={n} variant="outlined" />)}
        </Stack>
      );
      break;
  }
  return <Box>{label}{control}{helper}</Box>;
}
